import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { CdkConnectedOverlay, Overlay } from '@angular/cdk/overlay';
import { BaseFormControl, BaseSignalInput } from '@vallift/ngl';
import { CurrencyPickerPanel } from './currency-picker-panel';
import { CurrencyFieldValue, CurrencyOption } from './currency.catalog';
import {
  currencyAmountMessage,
  CURRENCY_DISPLAY_FALLBACK,
  DEFAULT_CURRENCIES,
  findCurrency,
  formatAmountForInput,
  getCurrencyShellClasses,
  isAllowedAmountKey,
  parseCurrencyAmount,
} from './currency.utils';

let nextCurrencyInputId = 0;

@Component({
  selector: 'app-currency-input',
  imports: [BaseSignalInput, CdkConnectedOverlay, CurrencyPickerPanel],
  templateUrl: './currency-input.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyInput extends BaseFormControl<CurrencyFieldValue | null> implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly overlay = inject(Overlay);

  readonly label = input('');
  readonly description = input('');
  readonly placeholder = input('0.00');
  readonly defaultCurrency = input<string | null>(null);
  readonly allowedCurrencies = input<readonly CurrencyOption[]>(DEFAULT_CURRENCIES);
  readonly allowChangeCurrency = input(true);
  readonly decimalPrecision = input<number | null>(null);
  readonly min = input<number | null>(null);
  readonly max = input<number | null>(null);

  readonly currencyChange = output<string>();
  readonly amountChange = output<number | null>();

  private readonly instanceId = ++nextCurrencyInputId;
  protected readonly repositionScrollStrategy = this.overlay.scrollStrategies.reposition();
  private amountFromInput: number | null | undefined = undefined;
  private defaultsApplied = false;

  @ViewChild('inputRef')
  inputRef!: ElementRef<HTMLInputElement>;

  @ViewChild('currencyButton')
  currencyButton!: ElementRef<HTMLButtonElement>;

  @ViewChild('fieldRoot')
  fieldRoot!: ElementRef<HTMLElement>;

  readonly amountText = signal('');
  readonly isPickerOpen = signal(false);
  readonly panelWidth = signal(320);
  readonly locallyTouched = signal(false);

  readonly inputId = computed(() => `currency-amount-${this.instanceId}`);
  readonly currencyButtonId = computed(() => `${this.inputId()}-currency`);
  readonly panelId = computed(() => `${this.currencyButtonId()}-panel`);

  readonly currencies = computed(() => this.allowedCurrencies());

  readonly selectedCurrency = computed(
    () => findCurrency(this.currencyType(), this.currencies()) ?? null,
  );

  readonly currencyType = computed(
    () =>
      this.value()?.currencyType ||
      this.defaultCurrency() ||
      CURRENCY_DISPLAY_FALLBACK,
  );

  readonly canChangeCurrency = computed(
    () => this.allowChangeCurrency() && !this.disabled() && !this.readonly(),
  );

  readonly resolvedPrecision = computed(() => {
    const override = this.decimalPrecision();
    if (override != null) {
      return override;
    }

    return this.selectedCurrency()?.decimalDigits ?? 2;
  });

  readonly localError = computed(() =>
    currencyAmountMessage(this.amountText(), {
      required: this.required(),
      min: this.min(),
      max: this.max(),
      decimalDigits: this.resolvedPrecision(),
    }),
  );

  readonly displayMessage = computed(() => {
    if (this.disabled()) {
      return null;
    }

    if (this.showError()) {
      return this.errorMessage() ?? this.localError();
    }

    if (this.locallyTouched() || this.touched()) {
      return this.localError();
    }

    return null;
  });

  readonly displayError = computed(() => this.displayMessage() != null);

  readonly controlFocused = computed(() => this.isFocused() || this.isPickerOpen());

  readonly shellClasses = computed(() =>
    getCurrencyShellClasses(this.disabled(), this.displayError(), this.controlFocused()),
  );

  constructor() {
    super();

    effect(() => {
      const current = this.value();
      const amount = current?.amount ?? null;

      untracked(() => {
        if (!this.defaultsApplied) {
          if (current == null) {
            this.value.set({
              currencyType: this.defaultCurrency() || CURRENCY_DISPLAY_FALLBACK,
              amount: null,
            });
          }
          this.defaultsApplied = true;
          this.syncAmountFromValue();
          return;
        }

        if (amount === this.amountFromInput) {
          return;
        }

        this.syncAmountFromValue();
      });
    });
  }

  ngOnInit(): void {
    if (!this.defaultsApplied) {
      if (this.value() == null) {
        this.value.set({
          currencyType: this.defaultCurrency() || CURRENCY_DISPLAY_FALLBACK,
          amount: null,
        });
      }
      this.defaultsApplied = true;
      this.syncAmountFromValue();
    }
  }

  togglePicker(): void {
    if (this.isPickerOpen()) {
      this.closePicker();
      return;
    }

    this.openPicker();
  }

  openPicker(): void {
    if (!this.canChangeCurrency() || this.isPickerOpen()) {
      return;
    }

    this.setPanelWidth();
    this.isPickerOpen.set(true);
    this.onFocus();
    this.cdr.detectChanges();
  }

  closePicker(): void {
    if (!this.isPickerOpen()) {
      return;
    }

    this.isPickerOpen.set(false);
    this.currencyButton?.nativeElement.focus();
  }

  onCurrencySelected(currency: CurrencyOption): void {
    if (!this.canChangeCurrency()) {
      this.closePicker();
      return;
    }

    const current = this.snapshot();
    this.closePicker();

    if (currency.code === current.currencyType) {
      this.inputRef?.nativeElement.focus();
      return;
    }

    this.setValue({
      currencyType: currency.code,
      amount: current.amount,
    });
    this.currencyChange.emit(currency.code);
    this.inputRef?.nativeElement.focus();
  }

  onCurrencyButtonKeyDown(event: KeyboardEvent): void {
    if (!this.canChangeCurrency()) {
      return;
    }

    if (
      !this.isPickerOpen() &&
      (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.openPicker();
    }
  }

  protected updateAmount(event: Event): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const text = (event.target as HTMLInputElement).value;
    this.amountText.set(text);

    const parsed = parseCurrencyAmount(text);
    const nextAmount = parsed.numeric ? parsed.amount : null;
    const current = this.snapshot();

    if (nextAmount === current.amount && nextAmount === this.amountFromInput) {
      return;
    }

    this.amountFromInput = nextAmount;
    this.setValue({
      currencyType: current.currencyType,
      amount: nextAmount,
    });
    this.amountChange.emit(nextAmount);
  }

  protected onAmountFocus(): void {
    this.onFocus();
  }

  protected onAmountBlur(event: FocusEvent): void {
    const trimmed = this.amountText().trim();
    if (trimmed !== this.amountText()) {
      this.amountText.set(trimmed);
      this.updateAmountFromText(trimmed);
    }

    const next = event.relatedTarget;
    if (next instanceof Node && this.fieldRoot?.nativeElement.contains(next)) {
      return;
    }

    if (this.isPickerOpen()) {
      return;
    }

    this.locallyTouched.set(true);
    this.onBlur();
  }

  protected onAmountKeyDown(event: KeyboardEvent): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const value = (event.target as HTMLInputElement).value;
    if (!isAllowedAmountKey(event, value)) {
      event.preventDefault();
    }
  }

  private updateAmountFromText(text: string): void {
    if (this.disabled() || this.readonly()) {
      return;
    }

    const parsed = parseCurrencyAmount(text);
    const nextAmount = parsed.numeric ? parsed.amount : null;
    const current = this.snapshot();
    this.amountFromInput = nextAmount;
    this.setValue({
      currencyType: current.currencyType,
      amount: nextAmount,
    });
    this.amountChange.emit(nextAmount);
  }

  private snapshot(): CurrencyFieldValue {
    return {
      currencyType: this.currencyType(),
      amount: this.value()?.amount ?? null,
    };
  }

  private syncAmountFromValue(): void {
    const amount = this.value()?.amount ?? null;
    this.amountFromInput = amount;
    const display = formatAmountForInput(amount);
    this.amountText.set(display);

    const input = this.inputRef?.nativeElement;
    if (input && input.value !== display) {
      input.value = display;
    }
  }

  private setPanelWidth(): void {
    const width = this.fieldRoot?.nativeElement.getBoundingClientRect().width ?? 0;
    this.panelWidth.set(Math.max(Math.round(width), 280));
  }
}

export type { CurrencyFieldValue, CurrencyOption } from './currency.catalog';
export {
  CURRENCY_DISPLAY_FALLBACK,
  DEFAULT_CURRENCIES,
  DEFAULT_CURRENCY_FIELD_CONFIG,
} from './currency.utils';
